import { useMemo } from 'react';
import { getDisplayLabel, parseLocationItems } from '../Utils/RouteDetailsUtils';
import { DeliveryCollectionSummaryData, DeliveryStatusSummary, RouteTableRow } from '../Types/RouteDetails.types';

interface RawLocation {
  input_address?: string;
  input_start_row?: string;
  waypoint_index?: number;
  location_id?: number | string;
  input_location_name?: string;
  waiting_time?: number;
  confirmation_driver_id?: unknown;
  delivery_date_time?: string;
  delivery_remark?: string;
  delivery_status?: string;
  comments?: string;
  items?: unknown;
  value?: number;
  delivery_value?: number | string | null;
  value_entries?: Record<string, number>;
  srn_value?: string | number | null;
  displayLabel?: string;
  [key: string]: unknown;
}

interface RouteDetailsData {
  actual_distance?: number;
  optimized_distance?: number;
  locations?: RawLocation[];
}

const FINANCIAL_TYPES = ['cash', 'cheque'];

const isFinancialType = (type: string): boolean => FINANCIAL_TYPES.some((t) => type.toLowerCase().includes(t));

export const useSortedLocations = (routeDetails: RouteDetailsData): RawLocation[] =>
  useMemo(() => {
    const nonEmpty = (routeDetails.locations ?? []).filter((loc) => !!loc.input_address);
    const sorted = [...nonEmpty].sort((a, b) => (a.waypoint_index ?? 0) - (b.waypoint_index ?? 0));

    if (sorted.length === 0) return [];

    return sorted.map((loc, index) => ({
      ...loc,
      displayLabel: getDisplayLabel(loc, index, sorted.length),
    }));
  }, [routeDetails.locations]);

export const useTableData = (
  sortedLocations: any[],
  confirmationFiles: any[],
  localComments: Record<number, string>,
): RouteTableRow[] =>
  useMemo(
    () =>
      sortedLocations.map((location: any, index: number) => {
        const filesForLocation =
          confirmationFiles?.filter((file: any) => file.location_id === location.location_id) || [];
        return {
          key: location.location_id ?? `${index}`,
          location,
          displayLabel: location.displayLabel ?? '',
          isStart: location.displayLabel === 'Start',
          isEnd: location.displayLabel === 'End',
          address: location.input_address,
          locationId: location.location_id,
          locationName: location.input_location_name,
          waitingTime: location.waiting_time,
          confirmationDateTime: location.confirmation_date_time,
          confirmationDriverId: location.confirmation_driver_id,
          deliveryDateTime: location.delivery_date_time,
          deliveryRemark: location.delivery_remark,
          deliveryStatus: location.delivery_status,
          comment:
            localComments[location.location_id] !== undefined
              ? localComments[location.location_id]
              : location.comments ?? null,
          value: location.value ?? 0,
          deliveredValue: location.delivered_value ?? 0,
          valueEntries:
            location.value_entries &&
            typeof location.value_entries === 'object' &&
            !Array.isArray(location.value_entries)
              ? location.value_entries
              : {},
          srnValue: location.srn_value,
          filesForLocation,
          items: parseLocationItems(location.items),
          waypointIndex: location.waypoint_index,
        };
      }),
    [sortedLocations, confirmationFiles, localComments],
  );

export const useDeliveryStatus = (routeDetails: RouteDetailsData): DeliveryStatusSummary =>
  useMemo(() => {
    const locations = routeDetails.locations ?? [];
    return locations.reduce<DeliveryStatusSummary>(
      (acc, loc) => {
        switch (loc.delivery_status) {
          case 'delivered':
            acc.delivered += 1;
            break;
          case 'skipped':
            acc.skipped += 1;
            break;
          default:
            acc.pending += 1;
            break;
        }
        return acc;
      },
      { delivered: 0, skipped: 0, pending: 0 },
    );
  }, [routeDetails.locations]);

export const useRouteAdherence = (routeDetails: RouteDetailsData): string =>
  useMemo(() => {
    const actual = routeDetails.actual_distance;
    const optimized = routeDetails.optimized_distance;
    if (!actual || !optimized || optimized === 0) return 'N/A';
    const adherence = Math.min(1, 2 - actual / optimized);
    const pct = adherence * 100;
    if (pct < 0) return '0.00%';
    return `${pct.toFixed(2)}%`;
  }, [routeDetails.actual_distance, routeDetails.optimized_distance]);

export const useDeliveryCollectionSummary = (tableData: any[]): DeliveryCollectionSummaryData =>
  useMemo((): DeliveryCollectionSummaryData => {
    const collectionByType: Record<string, number> = {};
    let totalValue = 0;
    let totalDelivered = 0;
    let totalDeliveryReturn = 0;

    tableData.forEach((row) => {
      totalValue += Number(row.value ?? 0);
      //  sum delivery return for delivered rows
      if (row.deliveryStatus === 'delivered') {
        const rowValue = Number(row.value ?? 0);
        const rowDelivered = Number(row.deliveredValue ?? 0);
        totalDeliveryReturn += rowValue - rowDelivered;
      }

      const { valueEntries } = row;
      if (valueEntries && typeof valueEntries === 'object' && !Array.isArray(valueEntries)) {
        Object.entries(valueEntries).forEach(([type, amount]) => {
          if (!collectionByType[type]) {
            collectionByType[type] = 0;
          }
          collectionByType[type] += Number(amount ?? 0);

          // ← Only sum financial types (cash, cheque) into totalDelivered
          if (isFinancialType(type)) {
            totalDelivered += Number(amount ?? 0);
          }
        });
      }
    });

    return {
      totalValue,
      totalDelivered,
      totalDeliveryReturn,
      collectionByType,
      remaining: totalValue - totalDelivered,
    };
  }, [tableData]);
